import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader, StatusBadge } from '@/components/Shared';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { money } from '@/lib/format';
import { ScanLine, Search, Send, PackageCheck, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function EmployeeScan() {
  const [scanOpen, setScanOpen] = useState(false);
  const [requestType, setRequestType] = useState('buy');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const boxRef = useRef(null);
  const debRef = useRef(null);

  const runSearch = useCallback(async (term) => {
    setSearching(true);
    try {
      const { data } = await api.get('/products', { params: term ? { q: term } : {} });
      setResults(data.slice(0, 40));
      setShowResults(true);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  // Debounced live search as the employee types.
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { runSearch(search); }, 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [search, runSearch]);

  // Close the results dropdown on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowResults(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const isOut = (p) => (p.quantity || 0) <= 0;
  const blockedForBuy = (p) => requestType === 'buy' && isOut(p);

  const selectProduct = (p) => {
    if (blockedForBuy(p)) { toast.error(`${p.name} is out of stock — cannot create a Buy Request`); return; }
    setProduct(p); setShowResults(false); setSearch(''); setQty(1);
  };

  // Barcode / scanner-gun result -> look up and select instantly.
  const onScan = async (code) => {
    setScanOpen(false);
    try {
      const { data } = await api.get('/products/lookup', { params: { code } });
      if (blockedForBuy(data)) { toast.error(`${data.name} is out of stock — cannot create a Buy Request`); return; }
      setProduct(data); setSearch('');
      toast.success(`Found ${data.name}`);
    } catch { toast.error('No product found for ' + code); }
  };

  const submit = async () => {
    if (!product) { toast.error('Select a product first'); return; }
    if (requestType === 'return' && !reason.trim()) { toast.error('Please enter a return reason'); return; }
    try {
      await api.post('/requests', { product_id: product.id, quantity: Number(qty), request_type: requestType, reason, note });
      toast.success(`${requestType === 'return' ? 'Return' : 'Buy'} request submitted — awaiting admin approval`);
      setProduct(null); setSearch(''); setQty(1); setNote(''); setReason('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title="Scan & Request" subtitle="Search or scan a product, then submit a Buy or Return request" testid="scan-header" />

      <Card className="p-6">
        {/* Request type first, so Buy vs Return selection rules apply */}
        <Label>Request Type</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          <Button type="button" variant={requestType === 'buy' ? 'default' : 'outline'} onClick={() => setRequestType('buy')} data-testid="scan-type-buy">Buy Request</Button>
          <Button type="button" variant={requestType === 'return' ? 'default' : 'outline'} onClick={() => setRequestType('return')} data-testid="scan-type-return">Return Request</Button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2">
          <Button variant="outline" className="w-full h-12" onClick={() => setScanOpen(true)} data-testid="scan-open-button"><ScanLine className="h-5 w-5 mr-2" /> Scan Barcode / Scanner Gun</Button>
        </div>

        {/* Live autocomplete search */}
        <div className="relative mt-4" ref={boxRef}>
          <Label>Search Product</Label>
          <div className="relative mt-1.5">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => { setShowResults(true); if (!results.length) runSearch(''); }}
              placeholder="Type name, SKU, barcode, brand or category…"
              className="pl-9"
              data-testid="product-search-input"
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
          </div>

          {showResults && (
            <div className="absolute z-30 mt-1 max-h-80 w-full overflow-auto rounded-lg border bg-white shadow-lg" data-testid="product-search-results">
              {results.length === 0 && !searching && (
                <div className="px-3 py-4 text-center text-sm text-muted-foreground">No products found</div>
              )}
              {results.map((p) => {
                const out = isOut(p);
                const disabled = blockedForBuy(p);
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => selectProduct(p)}
                    disabled={disabled}
                    className={`flex w-full items-center justify-between gap-3 border-b px-3 py-2.5 text-left last:border-0 transition-colors ${disabled ? 'cursor-not-allowed opacity-55' : 'hover:bg-[hsl(var(--accent))]'}`}
                    data-testid="product-search-result"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{p.name}</div>
                      <div className="truncate text-xs text-muted-foreground">SKU {p.sku}{p.category ? ` · ${p.category}` : ''}{p.brand ? ` · ${p.brand}` : ''}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-semibold">{money(p.sale_price)}</div>
                      {out
                        ? <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700" data-testid="product-out-of-stock">Out of Stock</span>
                        : <span className="text-xs text-muted-foreground" data-testid="product-available-qty">{p.quantity} available</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {product && (
        <Card className="p-6 mt-4" data-testid="scan-product-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2"><PackageCheck className="h-5 w-5 text-teal-700" /><h3 className="font-heading text-lg font-semibold">{product.name}</h3></div>
              <p className="text-sm text-muted-foreground mt-1">{product.brand ? `${product.brand} · ` : ''}SKU {product.sku}{product.category ? ` · ${product.category}` : ''}</p>
            </div>
            <button type="button" onClick={() => setProduct(null)} className="text-muted-foreground hover:text-foreground" data-testid="scan-clear-product"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
            <div><span className="text-muted-foreground">Available</span><div className="font-semibold">{product.quantity}</div></div>
            <div><span className="text-muted-foreground">Unit Price</span><div className="font-semibold">{money(product.sale_price)}</div></div>
            <div><span className="text-muted-foreground">Status</span><div className="mt-0.5"><StatusBadge kind="stock" value={product.stock_status} /></div></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div><Label>{requestType === 'return' ? 'Quantity to Return' : 'Quantity'}</Label><Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="scan-qty-input" className="mt-1.5" /></div>
            {requestType === 'return' && (
              <div><Label>Return Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Damaged, wrong item…" data-testid="scan-reason-input" className="mt-1.5" /></div>
            )}
          </div>
          <div className="mt-4"><Label>Note (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid="scan-note-input" /></div>
          <Button className="w-full mt-4" onClick={submit} disabled={requestType === 'buy' && product.quantity <= 0} data-testid="scan-submit-request-button"><Send className="h-4 w-4 mr-2" /> Submit {requestType === 'return' ? 'Return' : 'Buy'} Request</Button>
        </Card>
      )}

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={onScan} />
    </div>
  );
}
